// React
import { useState, useEffect, useMemo } from 'react';

// React Router
import { useSearchParams } from 'react-router-dom';

// Components
import FormFields from './FormFields';
import PortalSettings from './PortalSettings';
import CTabs from 'components/CTabs';

// MUI
import { Box, Stack } from '@mui/material';
import { CircularProgress } from '@mui/material';

// Utils
import xeniaApi from 'api/index';

// Context
import { RequestConfigContext, RequestConfigType } from './context';

const PublicRequestConfig = () => {
  const [config, setConfig] = useState<RequestConfigType | null>(null);
  const [localConfig, setLocalConfig] = useState<RequestConfigType | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();

  const configId = useMemo(
    () => searchParams.get('configId') ?? undefined,
    [searchParams],
  );

  const fetchConfig = async () => {
    try {
      const data = await xeniaApi.getPublicRequestConfig(undefined, configId);
      if (data) {
        setConfig(data.config);
        setLocalConfig(data.config);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  // update any key in the config helper
  const updateConfig = async (key: string, data: any) => {
    try {
      const updatedData = {
        ...(localConfig ?? {}),
        [key]: data,
      };
      setLocalConfig(updatedData as any);
      const updatedConfig = await xeniaApi.patchPublicRequestConfig({
        configId: config?.id as string,
        patchData: {
          [key]: data,
        },
      });
      setConfig(updatedConfig);
    } catch (error) {
      setLocalConfig(config);
    }
  };

  // update visibility for form fields and map to database schema
  const handleUpdateFieldVisibility = async (
    field: string,
    visibility: any,
  ) => {
    const visibilityMap = {
      Required: {
        required: true,
        show: true,
      },
      Optional: {
        required: false,
        show: true,
      },
      Hidden: {
        required: false,
        show: false,
      },
    };
    await updateConfig('fields', {
      ...localConfig?.fields,
      [field]: visibilityMap[visibility],
    });
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const contextValue = {
    config: localConfig,
    updateConfig,
    handleUpdateFieldVisibility,
  };

  return (
    <RequestConfigContext.Provider value={contextValue}>
      <Box sx={{ pt: '4px' }}>
        {loading || !config ? (
          <Stack
            width={'100%'}
            height={'300px'}
            alignItems={'center'}
            justifyContent={'center'}
          >
            <CircularProgress />
          </Stack>
        ) : (
          <CTabs
            rootSx={{
              '& .MuiTabs-scroller': {
                marginLeft: '24px',
              },
            }}
            data={[
              {
                label: 'Fields',
                content: <FormFields />,
              },
              {
                label: 'Portal Settings',
                content: <PortalSettings />,
              },
            ]}
          />
        )}
      </Box>
    </RequestConfigContext.Provider>
  );
};

export default PublicRequestConfig;
